import { objectSpread2 as _objectSpread2 } from '../../_virtual/_rollupPluginBabelHelpers.js';
import ResizeObserver from 'resize-observer-polyfill';
import Portal from '../portal/portal.js';
import FocusTrap from '../../utils/focus-trap.js';
import PendoIcon from '../icon/pendo-icon.vue.js';
import PendoButton from '../button/pendo-button.vue.js';
import { setStyles, getTopZindex } from '../../utils/dom.js';
import { hasSlot } from '../../utils/utils.js';
import { enableBodyScroll, disableBodyScroll } from '../../utils/body-scroll-lock.js';
import { getBoundAttributes, parseSizeType, getHeight, getWidth, clampValueToWindowOrMaxWidth, clampValueToWindowOrMaxHeight, getModalTopLeftPosition } from './utils.js';

function _await(value, then, direct) {
  if (direct) {
    return then ? then(value) : value;
  }
  if (!value || !value.then) {
    value = Promise.resolve(value);
  }
  return then ? value.then(then) : value;
}
let animatingZIndex = 0;
function _async(f) {
  return function () {
    for (var args = [], i = 0; i < arguments.length; i++) {
      args[i] = arguments[i];
    }
    try {
      return Promise.resolve(f.apply(this, args));
    } catch (e) {
      return Promise.reject(e);
    }
  };
}
const baseZindex = 2001;
var script = {
  name: 'PendoModal',
  components: {
    Portal,
    PendoIcon,
    PendoButton,
    FocusTrap
  },
  model: {
    prop: 'visible',
    event: 'close'
  },
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    actionButtons: {
      type: [Array, Boolean],
      default: () => []
    },
    type: {
      type: String,
      default: null,
      validator: type => ['confirmation'].includes(type)
    },
    title: {
      type: String,
      default: ''
    },
    message: {
      type: String,
      default: null
    },
    confirmButtonConfig: {
      type: Object,
      default: () => ({})
    },
    cancelButtonConfig: {
      type: Object,
      default: () => ({})
    },
    showBackdrop: {
      type: Boolean,
      default: true
    },
    appendToBody: {
      type: Boolean,
      default: true
    },
    showClose: {
      type: Boolean,
      default: true
    },
    clickToClose: {
      type: Boolean,
      default: false
    },
    escToClose: {
      type: Boolean,
      default: false
    },
    minWidth: {
      type: Number,
      default: 200
    },
    minHeight: {
      type: Number,
      default: 0
    },
    maxWidth: {
      type: [Number, String],
      default: Infinity
    },
    maxHeight: {
      type: [Number, String],
      default: Infinity
    },
    width: {
      type: [Number, String],
      default: 400
    },
    height: {
      type: [Number, String],
      default: 400
    },
    pivotX: {
      type: Number,
      default: 0.5
    },
    pivotY: {
      type: Number,
      default: 0.5
    },
    focusTrapConfig: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      zIndex: 0,
      show: false,
      mount: false,
      id: `pendo-modal-${this._uid}`,
      appendToTarget: 'body',
      isFocusTrapActive: false
    };
  },
  computed: {
    inheritedClasses() {
      if (this.mount) {
        return [this.$vnode.data.staticClass, this.$vnode.data.class];
      }
      return '';
    },
    inheritedAttrs() {
      const scopedStyleAttr = this.$parent.$options._scopeId;
      const attrs = getBoundAttributes(this.$vnode);
      if (scopedStyleAttr) {
        attrs[scopedStyleAttr] = '';
      }
      return attrs;
    },
    showCloseButton() {
      if (this.type === 'confirmation') {
        return this.$options.propsData.showClose || false;
      }
      return this.showClose;
    },
    confirmButtonAttrs() {
      const confirmButtonDefaults = {
        theme: 'app',
        type: 'primary',
        label: 'Confirm'
      };
      return _objectSpread2(_objectSpread2({}, confirmButtonDefaults), this.confirmButtonConfig);
    },
    cancelButtonAttrs() {
      const cancelButtonDefaults = {
        theme: 'app',
        type: 'secondary',
        label: 'Cancel'
      };
      return _objectSpread2(_objectSpread2({}, cancelButtonDefaults), this.cancelButtonConfig);
    },
    hasFooter() {
      const hasFooterSlot = hasSlot(this, 'footer');
      const hasActionButtons = Boolean(this.actionButtons);
      return hasFooterSlot || hasActionButtons;
    },
    isAutoHeight() {
      if (this.type === 'confirmation' && !this.$options.propsData.height) {
        return true;
      }
      return parseSizeType(this.height).type === 'auto';
    }
  },
  watch: {
    visible: {
      handler() {
        if (this.visible) {
          this.setAppendToTarget();
          this.mount = true;
          this.$nextTick(() => {
            this.show = true;
          });
        } else if (this.show) {
          this.show = false;
        }
      },
      immediate: true
    }
  },
  mounted() {
    this.resizeObserver = new ResizeObserver(() => {
      requestAnimationFrame(() => {
        this.setModalStyle();
      });
    });
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.setModalStyle);
    enableBodyScroll(this.id);
    this.resizeObserver.disconnect();
  },
  methods: {
    setAppendToTarget() {
      const shouldAttachToParentNode = !this.appendToBody && this.$el && this.$el.parentNode;
      this.appendToTarget = shouldAttachToParentNode ? this.$el.parentNode : 'body';
    },
    setModalStyle() {
      if (this.visible && this.$refs.modal) {
        const maxHeight = getHeight(this.maxHeight);
        const maxWidth = getWidth(this.maxWidth);
        const trueModalWidth = clampValueToWindowOrMaxWidth(this.width, this.minWidth, this.maxWidth);
        const trueModalHeight = this.isAutoHeight ? this.$refs.modal.clientHeight : clampValueToWindowOrMaxHeight(this.height, this.minHeight, this.maxHeight);
        const modalPosition = getModalTopLeftPosition({
          pivotY: this.pivotY,
          pivotX: this.pivotX,
          trueModalWidth,
          trueModalHeight
        });
        const styles = {
          top: `${modalPosition.top}px`,
          left: `${modalPosition.left}px`,
          width: `${trueModalWidth}px`,
          height: this.isAutoHeight ? 'auto' : `${trueModalHeight}px`
        };
        if (maxHeight !== 0) {
          styles.maxHeight = maxHeight;
        }
        if (maxWidth !== 0) {
          styles.maxWidth = maxWidth;
        }
        if (this.$refs.modal) {
          setStyles(this.$refs.modal, styles);
        }
      }
    },
    removeWindowResizeListeners() {
      window.removeEventListener('resize', this.setModalStyle);
    },
    initWindowResizeListeners() {
      window.addEventListener('resize', this.setModalStyle);
    },
    onClickOutside(event) {
      if (this.clickToClose && !this.$refs.modal.contains(event.target)) {
        this.close();
      }
    },
    onEscapeKeydown() {
      if (this.escToClose) {
        this.close();
      }
    },
    beforeOpen() {
      const lastZindex = getTopZindex();
      if (animatingZIndex) {
        this.zIndex = animatingZIndex + 2;
      } else {
        this.zIndex = lastZindex === 0 ? baseZindex : lastZindex + 2;
      }
      animatingZIndex = this.zIndex;
    },
    open() {
      disableBodyScroll(this.id);
      this.initWindowResizeListeners();
      this.$emit('open');
      this.$emit('update:visible', true);
      this.$nextTick(() => {
        this.resizeObserver.observe(this.$refs.modal);
      });
    },
    afterOpen() {
      this.isFocusTrapActive = true;
      this.$emit('opened');
    },
    beforeClose() {
      this.removeWindowResizeListeners();
      this.resizeObserver.unobserve(this.$refs.modal);
    },
    close() {
      this.$emit('update:visible', false);
      if (this.show) {
        this.$emit('close');
      }
    },
    afterClose: _async(function () {
      const _this = this;
      _this.zIndex = 0;
      _this.mount = false;
      return _await(_this.$nextTick(), function () {
        requestAnimationFrame(() => {
          enableBodyScroll(_this.id);
          _this.isFocusTrapActive = false;
          animatingZIndex = 0;
          _this.$emit('closed');
        });
      });
    }),
    handleActionButtonClick(index, event) {
      let source = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : 'click';
      const button = this.actionButtons[index];
      if (button && typeof button.handler === 'function') {
        button.handler(index, event, {
          source
        });
      } else {
        this.close();
      }
    }
  }
};

export { script as default };
//# sourceMappingURL=pendo-modal.vue2.js.map
